import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive, Bell, BellOff, FileText, Hash, House, LogOut, Menu, MessageCircleMore,
  MoreHorizontal, Paperclip, Plus, Search, Send, Settings, Smile, UserPlus, Users, X,
} from "lucide-react";

import { apiRequest, uploadAttachment } from "./api.js";
import AuthPanel from "./AuthPanel.jsx";
import Home from "./Home.jsx";
import Modal from "./Modal.jsx";

const roomColors = ["#5f7f68", "#a36f4a", "#6f789d", "#8c6f82", "#5a8390"];
const emojis = ["😀", "😊", "👍", "🙏", "🎉", "❤️", "✨", "😂", "👏", "✅"];

function formatTime(value) {
  if (!value) return "";
  const normalized = String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ko-KR", { hour: "numeric", minute: "2-digit" }).format(date);
}

function readableBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function App() {
  const [screen, setScreen] = useState("home");
  const [backend, setBackend] = useState({ checking: true, database: "unconfigured", storage: "unconfigured" });
  const [authChecking, setAuthChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const fileInputRef = useRef(null);
  const messageScrollRef = useRef(null);

  const activeRoom = rooms.find((room) => room.id === activeId) ?? null;
  const filteredRooms = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase("ko");
    return keyword ? rooms.filter((room) => `${room.name} ${room.preview || ""}`.toLocaleLowerCase("ko").includes(keyword)) : rooms;
  }, [query, rooms]);

  useEffect(() => {
    Promise.all([
      apiRequest("/api/health").catch(() => ({ database: "unconfigured", storage: "unconfigured" })),
      apiRequest("/api/auth/session").catch(() => ({ authenticated: false })),
    ]).then(([health, session]) => {
      setBackend({ checking: false, database: health.database, storage: health.storage });
      setUser(session.authenticated ? session.user : null);
      setAuthChecking(false);
    });
  }, []);

  const loadRooms = useCallback(async () => {
    if (!user) return;
    try {
      const payload = await apiRequest("/api/rooms");
      setRooms(payload.rooms);
      setActiveId((current) => payload.rooms.some((room) => room.id === current) ? current : payload.rooms[0]?.id || "");
    } catch (error) {
      if (error.status === 401) setUser(null);
      else setNotice(error.message);
    }
  }, [user]);

  const loadMessages = useCallback(async (quiet = false) => {
    if (!activeId || !user) return;
    if (!quiet) setLoadingMessages(true);
    try {
      const payload = await apiRequest(`/api/rooms/${activeId}/messages?limit=80`);
      setMessages(payload.messages);
      const lastMessage = payload.messages.at(-1);
      if (lastMessage) apiRequest(`/api/rooms/${activeId}/read`, { method: "POST", body: JSON.stringify({ messageId: lastMessage.id }) }).catch(() => {});
    } catch (error) {
      setNotice(error.message);
    } finally {
      if (!quiet) setLoadingMessages(false);
    }
  }, [activeId, user]);

  useEffect(() => { if (screen === "chat" && user) loadRooms(); }, [loadRooms, screen, user]);
  useEffect(() => {
    if (!activeId || screen !== "chat" || !user) return undefined;
    loadMessages();
    const timer = setInterval(() => { loadMessages(true); loadRooms(); }, 4000);
    return () => clearInterval(timer);
  }, [activeId, loadMessages, loadRooms, screen, user]);
  useEffect(() => { messageScrollRef.current?.scrollTo({ top: messageScrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (!notice) return undefined; const timer = setTimeout(() => setNotice(""), 3500); return () => clearTimeout(timer); }, [notice]);

  async function sendMessage(event, attachmentId = null) {
    event?.preventDefault?.();
    if (!activeId || sending) return;
    const body = draft.trim();
    if (!body && !attachmentId) return;
    setSending(true);
    try {
      await apiRequest(`/api/rooms/${activeId}/messages`, { method: "POST", body: JSON.stringify({ body, attachmentId }) });
      setDraft("");
      setEmojiOpen(false);
      await Promise.all([loadMessages(true), loadRooms()]);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSending(false);
    }
  }

  async function handleFile(file) {
    if (!file || !activeId) return;
    setSending(true);
    setNotice("첨부파일을 올리고 있습니다…");
    try {
      const payload = await uploadAttachment(activeId, file);
      setSending(false);
      await sendMessage(null, payload.attachment.id);
      setNotice("첨부파일을 보냈습니다.");
    } catch (error) {
      setNotice(error.message);
      setSending(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function createRoom(name) {
    const payload = await apiRequest("/api/rooms", { method: "POST", body: JSON.stringify({ name }) });
    await loadRooms();
    setActiveId(payload.room.id);
    setModal(null);
  }

  async function toggleMute() {
    if (!activeRoom) return;
    const muted = !Boolean(activeRoom.muted);
    await apiRequest(`/api/rooms/${activeRoom.id}/settings`, { method: "PATCH", body: JSON.stringify({ muted }) });
    setRooms((current) => current.map((room) => room.id === activeRoom.id ? { ...room, muted: muted ? 1 : 0 } : room));
    setNotice(muted ? "이 대화방 알림을 껐습니다." : "이 대화방 알림을 켰습니다.");
  }

  async function logout() {
    await apiRequest("/api/auth/logout", { method: "POST" });
    setUser(null); setRooms([]); setMessages([]); setActiveId(""); setModal(null); setScreen("home");
  }

  if (screen === "home") return <Home backend={backend} onEnter={() => setScreen("chat")} />;
  if (authChecking) return <main className="auth-page"><p>로그인 상태를 확인하고 있습니다…</p></main>;
  if (!user) return <AuthPanel onAuthenticated={(nextUser) => { setUser(nextUser); setScreen("chat"); }} onBack={() => setScreen("home")} />;

  const backendReady = backend.database === "ready" && backend.storage === "ready";

  return (
    <main className="app-shell">
      <aside className={`sidebar ${sidebarOpen ? "sidebar--open" : ""}`} aria-label="대화 목록">
        <div className="brand-row">
          <button className="brand-mark" type="button" onClick={() => setScreen("home")} aria-label="홈으로 이동"><MessageCircleMore size={21} /></button>
          <div><strong>채팅 서비스</strong><span>{backendReady ? "D1·R2 연결됨" : "연결 확인 중"}</span></div>
          <button className="icon-button mobile-close" type="button" onClick={() => setSidebarOpen(false)} aria-label="대화 목록 닫기"><X size={20} /></button>
        </div>
        <button className="new-chat-button" type="button" onClick={() => setModal("new-room")}><Plus size={18} /> 새 대화</button>
        <label className="search-box"><Search size={17} aria-hidden="true" /><span className="sr-only">대화 검색</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="대화 검색" /></label>
        <div className="section-label"><span>대화 {filteredRooms.length}</span></div>
        <nav className="conversation-list">
          {filteredRooms.map((room, index) => (
            <button type="button" className={`conversation-card ${activeId === room.id ? "conversation-card--active" : ""}`} key={room.id} onClick={() => { setActiveId(room.id); setSidebarOpen(false); }}>
              <span className="conversation-avatar" style={{ background: roomColors[index % roomColors.length] }}>{room.name.slice(0, 1)}</span>
              <span className="conversation-copy"><span className="conversation-title"><strong>{room.name}</strong><small>{formatTime(room.latestAt)}</small></span><span className="conversation-preview">{room.preview || "첫 메시지를 보내보세요."}</span></span>
            </button>
          ))}
          {!filteredRooms.length && <p className="empty-sidebar">{query ? "검색 결과가 없습니다." : "새 대화를 만들어보세요."}</p>}
        </nav>
        <div className="sidebar-footer">
          <div className="profile-avatar">{user.displayName.slice(0, 1)}</div>
          <div><strong>{user.displayName}</strong><span className="connection-state"><i className={`status-dot status-dot--${backendReady ? "ready" : "checking"}`} />@{user.loginId}</span></div>
          <button className="icon-button" type="button" onClick={() => setModal("profile")} aria-label="설정"><Settings size={19} /></button>
        </div>
      </aside>
      {sidebarOpen && <button type="button" className="backdrop" onClick={() => setSidebarOpen(false)} aria-label="대화 목록 닫기" />}

      <section className="chat-panel" aria-label={activeRoom ? `${activeRoom.name} 대화방` : "대화방"}>
        <header className="chat-header">
          <button className="icon-button menu-button" type="button" onClick={() => setSidebarOpen(true)} aria-label="대화 목록 열기"><Menu size={21} /></button>
          <div className="room-identity"><span className="room-icon"><Hash size={18} /></span><div><strong>{activeRoom?.name || "대화를 선택하세요"}</strong><span>{activeRoom ? `멤버 ${activeRoom.memberCount || 1}명 · ${activeRoom.kind === "self" ? "나만의 메모 공간" : "함께하는 대화 공간"}` : "왼쪽에서 대화방을 선택하세요"}</span></div></div>
          <div className="header-actions">
            <button className="icon-button" type="button" onClick={() => setScreen("home")} aria-label="홈으로 이동"><House size={19} /></button>
            <button className={`icon-button ${activeRoom?.muted ? "icon-button--active" : ""}`} type="button" onClick={toggleMute} disabled={!activeRoom} aria-label={activeRoom?.muted ? "알림 켜기" : "알림 끄기"} aria-pressed={Boolean(activeRoom?.muted)}>{activeRoom?.muted ? <BellOff size={19} /> : <Bell size={19} />}</button>
            <button className="member-button" type="button" onClick={() => setModal("members")} disabled={!activeRoom}><Users size={18} /><span>{activeRoom?.memberCount || 0}</span></button>
            <button className="icon-button" type="button" onClick={() => setModal("room-settings")} disabled={!activeRoom} aria-label="대화방 설정"><MoreHorizontal size={20} /></button>
          </div>
        </header>

        <div className="message-scroll" ref={messageScrollRef}>
          {!activeRoom ? <div className="room-welcome"><span><MessageCircleMore size={24} /></span><h1>대화를 시작해보세요</h1><p>새 대화 버튼으로 첫 공간을 만들 수 있습니다.</p></div> : (
            <><div className="room-welcome"><span><Hash size={24} /></span><h1>{activeRoom.name}</h1><p>{activeRoom.kind === "self" ? "생각과 자료를 나에게 보내 보관하세요." : "멤버들과 메시지와 파일을 안전하게 나눠보세요."}</p></div><div className="day-divider"><span>{loadingMessages ? "불러오는 중…" : "최근 메시지"}</span></div>
              <div className="messages">{messages.map((message) => { const mine = message.authorId === user.id; return <article className={`message ${mine ? "message--mine" : ""}`} key={message.id}><div className={`message-avatar message-avatar--${mine ? "mine" : "other"}`}>{message.authorName?.slice(0, 1) || "?"}</div><div className="message-content"><div className="message-meta"><strong>{mine ? "나" : message.authorName}</strong><time>{formatTime(message.createdAt)}</time></div><p>{message.body}</p>{message.attachments?.map((attachment) => <a className="attachment-card" href={`/api/attachments/${attachment.id}`} target="_blank" rel="noreferrer" key={attachment.id}><FileText size={18} /><span><strong>{attachment.originalName}</strong><small>{readableBytes(attachment.byteSize)}</small></span></a>)}</div></article>; })}{!loadingMessages && !messages.length && <p className="empty-messages">아직 메시지가 없습니다. 첫 인사를 남겨보세요.</p>}</div></>
          )}
        </div>

        <form className="composer" onSubmit={sendMessage}>
          <div className="composer-inner">
            <button className="icon-button" type="button" onClick={() => fileInputRef.current?.click()} disabled={!activeRoom || sending} aria-label="파일 첨부"><Paperclip size={20} /></button><input ref={fileInputRef} className="sr-only" type="file" onChange={(event) => handleFile(event.target.files?.[0])} />
            <label><span className="sr-only">메시지 입력</span><textarea value={draft} onChange={(event) => setDraft(event.target.value)} disabled={!activeRoom || sending} placeholder={activeRoom ? `${activeRoom.name}에 메시지 보내기` : "대화방을 선택하세요"} rows="1" onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); sendMessage(event); } }} /></label>
            <div className="emoji-wrap"><button className="icon-button emoji-button" type="button" onClick={() => setEmojiOpen((current) => !current)} disabled={!activeRoom} aria-label="이모지" aria-expanded={emojiOpen}><Smile size={20} /></button>{emojiOpen && <div className="emoji-popover">{emojis.map((emoji) => <button type="button" key={emoji} onClick={() => { setDraft((current) => `${current}${emoji}`); setEmojiOpen(false); }}>{emoji}</button>)}</div>}</div>
            <button className="send-button" type="submit" disabled={!activeRoom || !draft.trim() || sending} aria-label="메시지 보내기"><Send size={19} /></button>
          </div><p>Enter로 보내기 · Shift + Enter로 줄바꿈 · 파일 최대 10MB</p>
        </form>
      </section>
      {notice && <div className="toast" role="status">{notice}</div>}
      {modal === "new-room" && <NewRoomModal onClose={() => setModal(null)} onCreate={createRoom} />}
      {modal === "profile" && <ProfileModal user={user} onClose={() => setModal(null)} onSaved={setUser} onLogout={logout} />}
      {modal === "members" && activeRoom && <MembersModal room={activeRoom} currentUser={user} onClose={() => setModal(null)} onChanged={loadRooms} />}
      {modal === "room-settings" && activeRoom && <RoomSettingsModal room={activeRoom} onClose={() => setModal(null)} onChanged={async () => { await loadRooms(); setModal(null); }} />}
    </main>
  );
}

function NewRoomModal({ onClose, onCreate }) {
  const [name, setName] = useState(""); const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  return <Modal title="새 대화 만들기" onClose={onClose}><form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await onCreate(name); } catch (requestError) { setError(requestError.message); setSaving(false); } }}><label><span>대화방 이름</span><input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 우리 팀 프로젝트" maxLength="50" required /></label>{error && <p className="form-error">{error}</p>}<button className="modal-primary" type="submit" disabled={saving || !name.trim()}>{saving ? "만드는 중…" : "대화 만들기"}</button></form></Modal>;
}

function ProfileModal({ user, onClose, onSaved, onLogout }) {
  const [form, setForm] = useState({ displayName: user.displayName, bio: user.bio || "" }); const [error, setError] = useState("");
  return <Modal title="내 프로필 설정" onClose={onClose}><form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setError(""); try { const payload = await apiRequest("/api/auth/profile", { method: "PATCH", body: JSON.stringify(form) }); onSaved(payload.user); onClose(); } catch (requestError) { setError(requestError.message); } }}><label><span>표시 이름</span><input value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} maxLength="30" /></label><label><span>소개</span><textarea value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} maxLength="120" placeholder="간단한 소개를 남겨보세요." /></label>{error && <p className="form-error">{error}</p>}<button className="modal-primary" type="submit">저장</button><button className="modal-danger-outline" type="button" onClick={onLogout}><LogOut size={16} /> 로그아웃</button></form></Modal>;
}

function MembersModal({ room, currentUser, onClose, onChanged }) {
  const [members, setMembers] = useState([]); const [loginId, setLoginId] = useState(""); const [error, setError] = useState("");
  const canAdd = ["owner", "moderator"].includes(room.role) && room.kind !== "self";
  const load = useCallback(async () => { const payload = await apiRequest(`/api/rooms/${room.id}/members`); setMembers(payload.members); }, [room.id]);
  useEffect(() => { load().catch((requestError) => setError(requestError.message)); }, [load]);
  return <Modal title={`멤버 · ${room.name}`} onClose={onClose} wide><div className="member-list">{members.map((member) => <div className="member-row" key={member.id}><span className="member-avatar">{member.displayName.slice(0, 1)}</span><div><strong>{member.displayName}{member.id === currentUser.id ? " (나)" : ""}</strong><small>@{member.loginId}</small></div><em>{member.role === "owner" ? "방장" : member.role === "moderator" ? "관리자" : "멤버"}</em></div>)}</div>{canAdd && <form className="member-add" onSubmit={async (event) => { event.preventDefault(); setError(""); try { await apiRequest(`/api/rooms/${room.id}/members`, { method: "POST", body: JSON.stringify({ loginId }) }); setLoginId(""); await load(); await onChanged(); } catch (requestError) { setError(requestError.message); } }}><label><span>아이디로 멤버 추가</span><div><input value={loginId} onChange={(event) => setLoginId(event.target.value)} placeholder="추가할 사용자 아이디" /><button type="submit"><UserPlus size={16} /> 추가</button></div></label></form>}{error && <p className="form-error modal-error">{error}</p>}</Modal>;
}

function RoomSettingsModal({ room, onClose, onChanged }) {
  const [name, setName] = useState(room.name); const [error, setError] = useState("");
  return <Modal title="대화방 설정" onClose={onClose}><form className="modal-form" onSubmit={async (event) => { event.preventDefault(); setError(""); try { await apiRequest(`/api/rooms/${room.id}/settings`, { method: "PATCH", body: JSON.stringify({ name }) }); await onChanged(); } catch (requestError) { setError(requestError.message); } }}><label><span>대화방 이름</span><input value={name} onChange={(event) => setName(event.target.value)} disabled={room.role !== "owner" || room.kind === "self"} maxLength="50" /></label>{error && <p className="form-error">{error}</p>}{room.role === "owner" && room.kind !== "self" && <><button className="modal-primary" type="submit">이름 저장</button><button className="modal-danger-outline" type="button" onClick={async () => { if (!window.confirm("이 대화방을 보관할까요? 목록에서 숨겨집니다.")) return; await apiRequest(`/api/rooms/${room.id}/settings`, { method: "PATCH", body: JSON.stringify({ archived: true }) }); await onChanged(); }}><Archive size={16} /> 대화방 보관</button></>} {room.kind === "self" && <p className="modal-help">나에게 보내기 방은 계정과 함께 유지됩니다.</p>}</form></Modal>;
}

export default App;
